import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { ReactSelectForm, CustomButton, ConfirmModal } from '../../../../../components';
import { AddIcon } from '../../../../../assets/icons/AddIcon';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { GroupSection } from '../GroupSection/GroupSection';
import { MatchSection } from '../MatchSection';
import {
  Group,
  MatchResult,
  Team,
  User,
  TournamentGroup,
  TournamentCategory,
} from '../../../../../types/tournament';
import { playTagTypeConverter } from '../../../../../utils/playTagTypeConverter';
import { buildTournamentBracketCellsForClassPlay } from '../../../../../utils/bracketCellBulkSave';
import { checkStartedDownstreamDeletes } from '../../../../../utils/bracketCellDownstreamDelete.util';
import { deepCloneSerializable } from '../../../../../utils/deepCloneSerializable';
import {
  collectPendingDeletedMatchIds,
  mergePendingBracketCellDeletes,
} from '../../../../../utils/bracketCellPendingDeletes.util';
import {
  boxLeaguePlayGroupsReadyForGenerate,
  getFormTournamentGroupsForPlay,
  groupsHaveBoxLeagueRoundMeta,
} from '../../../../../utils/boxLeaguePlayHelpers';
import { updatePlay } from '../../../../../features/tournament/actionCreators';
import { ClassFormatEnum, ClassTypeEnum } from '../../../../../types/enums';
import { selectClass } from '../../../../../features/class/classSelectors';
import { TagIdsEnum } from '../../../../../constant/navigation';
import { createAndSortOptionsForClassSelect } from '../../../CreativeTourDetail.helpers';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchClassPlayData } from '../../../../../apis/class';
import {
  fetchClassFullTeamExtras,
  fetchClassesByTournamentIdAndClassId,
} from '../../../../../apis/tournament';
import { getTournamentTimezone } from '../../../Schedule/components/schedule.helpers';
import { toDatetimeLocalValue } from '../../../../../utils/tournamentDateTime';
import { getTimezoneOptions } from '../../../../../utils/timezone';
import { selectTournamentData } from '../../../../../features/tournament/tournamentSelectors';

import './PlayTag.scss';
import { classSetSingleClass } from '../../../../../features/class/classSlice';
import { ScoreCardDownload } from '../ScoreCardDownload';
import { toBoolean } from '../../../../../utils/common.helpers';
import { Notification } from '../../../../../hooks/useNotification';
import { TypeError } from '../../../../../types/enums';

export interface IMatch {
  readonly id: string | number;
  name: string;
  type: number;
  startDate: string;
  scheduleDate?: string | null;
  matchId: string;
  status?: number; // should not send to backend (separate route for update)
  homeTeam: Team | null;
  awayTeam: Team | null;
  homePlayer1: User | null;
  homePlayer2: User | null;
  awayPlayer1: User | null;
  awayPlayer2: User | null;
  homeTeamPlaceholder: string | null;
  awayTeamPlaceholder: string | null;
  court: number | null;
  results?: Array<MatchResult>;
  tournamentGroup?: TournamentGroup;
  tournamentGroupId: number | null;
  tournamentCategory?: TournamentCategory;
  tournamentCategoryId: number | null;
  isGold: string;
  bracket: number;
  sortKey: number | null;
  bracketCellId?: string | null;
  bracketCellMatchIndex?: number;
  savedBracketCellMatchIndex?: number;
  bracketCellMatchCount?: number;
  bracketCellStatus?: number;
  bracketCellProgressionMode?: number;
  bracketCellWinnerTeamId?: number | null;
  bracketCellLoserTeamId?: number | null;
  bracketCellProgressionExplanation?: Record<string, unknown> | null;
  bracketCellMatchSuperseded?: boolean;
  isTiebreaker?: boolean;
  tiebreakerPending?: boolean;
}

export type BracketCellForm = {
  winnerTeamId?: number | '';
  loserTeamId?: number | '';
  progressionMode?: number | string;
  matchCount?: number;
  status?: number | string;
  round?: number | string;
  isGold?: boolean | string;
  bracket?: number | string;
  progressedAt?: string | null;
  progressionExplanation?: Record<string, unknown> | null;
  pendingDeletedMatchIds?: number[];
  isNewCell?: boolean;
};

export interface PlayValues {
  readonly classId: string | number;
  readonly type?: number;
  tournamentGroups: Array<Group>;
  tournamentMatches: { [key in number]: IMatch };
  tournamentCategories: Array<TournamentCategory>;
  bracketCells: Record<string, BracketCellForm>;
  pendingDeletedTopLevelMatchIds?: number[];
  pendingDeletedBracketCellIds?: string[];
  initialBracketCellIds?: string[];
}

function buildBracketCellsFromMatches(
  tournamentMatches: unknown[],
): Record<string, BracketCellForm> {
  const out: Record<string, BracketCellForm> = {};
  for (const raw of tournamentMatches) {
    const item = raw as Record<string, unknown>;
    const cid = item.bracketCellId;
    if (typeof cid === 'string' && cid && out[cid] === undefined) {
      const w = item.bracketCellWinnerTeamId;
      const l = item.bracketCellLoserTeamId;
      out[cid] = {
        winnerTeamId: typeof w === 'number' ? w : '',
        loserTeamId: typeof l === 'number' ? l : '',
        progressionMode:
          typeof item.bracketCellProgressionMode === 'number'
            ? item.bracketCellProgressionMode
            : undefined,
        matchCount:
          typeof item.bracketCellMatchCount === 'number' ? item.bracketCellMatchCount : undefined,
        status: typeof item.bracketCellStatus === 'number' ? item.bracketCellStatus : undefined,
        round: typeof item.type === 'number' ? item.type : undefined,
        isGold: item.isGold != null ? String(item.isGold) : undefined,
        bracket: item.bracket != null ? Number(item.bracket) : undefined,
        progressedAt: null,
        progressionExplanation:
          (item.bracketCellProgressionExplanation as Record<string, unknown> | null) ?? null,
        pendingDeletedMatchIds: [],
      };
    }
  }
  return out;
}

export const PlayTag = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const buttonPrefix = 'button-';
  const { id: tournamentId } = useAppSelector(selectTournamentData) || { id: null };

  const { currentClassId, classCollection } = useAppSelector(selectClass);

  const [currentClass, setCurrentClass] = useState<any>(null as unknown as any);
  const [loading, setLoading] = useState<boolean>(false);
  const pendingDeleteStateRef = useRef<{
    bracketCells: Record<string, BracketCellForm>;
    pendingDeletedTopLevelMatchIds: number[];
    pendingDeletedBracketCellIds: string[];
  }>({ bracketCells: {}, pendingDeletedTopLevelMatchIds: [], pendingDeletedBracketCellIds: [] });
  const playBaselineRef = useRef<PlayValues | null>(null);
  const pendingClassPlaySaveRef = useRef<PlayValues | null>(null);
  const [downstreamDeleteConfirmOpen, setDownstreamDeleteConfirmOpen] = useState(false);
  const { data } = useAppSelector((state) => state.tournament);

  const courts = data?.courts;
  const venueTimeZone = useMemo(
    () => getTournamentTimezone(data?.country, data?.timeZone),
    [data?.country, data?.timeZone]
  );
  const venueTimeZoneLabel = useMemo(
    () => getTimezoneOptions().find((opt) => opt.value === venueTimeZone)?.label ?? venueTimeZone,
    [venueTimeZone]
  );
  const pendingDeletedMatchIds = collectPendingDeletedMatchIds(
    pendingDeleteStateRef.current.bracketCells,
    pendingDeleteStateRef.current.pendingDeletedTopLevelMatchIds,
  );
  const pendingDeletedCellIds = new Set(
    pendingDeleteStateRef.current.pendingDeletedBracketCellIds,
  );
  const initialBracketCellIds = useMemo(() => {
    const ids = new Set<string>();
    for (const raw of currentClass?.tournamentMatches ?? []) {
      const cid = (raw as { bracketCellId?: string }).bracketCellId;
      if (typeof cid === 'string' && cid) {
        ids.add(cid);
      }
    }
    return [...ids];
  }, [currentClass?.tournamentMatches]);

  const initialFormikData = {
    classId: currentClassId,
    type: currentClass ? currentClass.type : null,
    tournamentGroups: currentClass
      ? getFormTournamentGroupsForPlay(currentClass)
      : [],
    tournamentCategories: currentClass
      ? (currentClass.tournamentCategories ?? []).map((category: TournamentCategory) => ({
          ...category,
          isActive: toBoolean(category.isActive),
        }))
      : [],
    tournamentMatches: currentClass
      ? currentClass.tournamentMatches?.reduce((acc: any, item: any) => {
          if (
            pendingDeletedMatchIds.has(item.id) ||
            (typeof item.bracketCellId === 'string' &&
              pendingDeletedCellIds.has(item.bracketCellId))
          ) {
            return acc;
          }
          const newItem: IMatch = {
            ...item,
            isTiebreaker: Boolean((item as { isTiebreaker?: boolean }).isTiebreaker),
            bracketCellProgressionExplanation:
              (item as { bracketCellProgressionExplanation?: Record<string, unknown> | null })
                .bracketCellProgressionExplanation ?? null,
            savedBracketCellMatchIndex: item.bracketCellMatchIndex ?? 1,
          };
          newItem.startDate = item.startDate
            ? toDatetimeLocalValue(item.startDate, venueTimeZone)
            : '';
          const resolvedCourt =
            item.court != null
              ? typeof item.court === 'object'
                ? item.court.id
                : item.court
              : item.courtId ?? null;
          newItem.court = resolvedCourt ?? null;
          acc[`${item.id}`] = newItem;
          return acc;
        }, {})
      : {},
    bracketCells: currentClass
      ? Object.fromEntries(
          Object.entries(
            mergePendingBracketCellDeletes(
              buildBracketCellsFromMatches(currentClass.tournamentMatches ?? []),
              pendingDeleteStateRef.current.bracketCells,
            ),
          ).filter(([cellId]) => !pendingDeletedCellIds.has(cellId)),
        )
      : {},
    pendingDeletedTopLevelMatchIds:
      pendingDeleteStateRef.current.pendingDeletedTopLevelMatchIds,
    pendingDeletedBracketCellIds:
      pendingDeleteStateRef.current.pendingDeletedBracketCellIds,
    initialBracketCellIds,
  } as PlayValues;

  useEffect(() => {
    if (!loading && currentClass) {
      playBaselineRef.current = deepCloneSerializable(initialFormikData);
    }
  }, [loading, currentClassId, currentClass?.tournamentMatches]);

  const playOptions = createAndSortOptionsForClassSelect(classCollection);

  const isBoxLeagueMode =
      (currentClass?.format === ClassFormatEnum.BOX_LEAGUE ||
        currentClass?.format === ClassFormatEnum.BOX_LEAGUE_ELIM) &&
      currentClass?.type !== ClassTypeEnum.TEAMS;

  const prefix = 'creativeDetail-';

  const isGenerateButton = !Object.values(initialFormikData.tournamentMatches).find(
    ({ tournamentGroupId }) => {
      return typeof tournamentGroupId !== 'number';
    }
  );

  const getClassData = async () => {
    if (typeof tournamentId === 'number' && typeof currentClassId === 'number') {
      try {
        setLoading(true);
        const data = await fetchClassPlayData(currentClassId);
        let merged = data;
        try {
          const extras = await fetchClassFullTeamExtras(currentClassId);
          merged = { ...merged, ...extras };
        } catch {
          // keep class payload without per-category standings
        }
        try {
          const { teams } = await fetchClassesByTournamentIdAndClassId(currentClassId);
          merged = { ...merged, teams: teams ?? [] };
        } catch {
          merged = { ...merged, teams: merged.teams ?? [] };
        }
        dispatch(classSetSingleClass(merged));
        setCurrentClass(merged);
        setLoading(false);
      } catch (e) {
        console.log(e);
        setLoading(false);
      }
    }
  };

  const saveClassPlay = async (values: PlayValues) => {
    if (typeof currentClassId !== 'number') return;
    try {
      await dispatch(
        updatePlay(
          currentClassId,
          {
            ...playTagTypeConverter(values, currentClass ?? undefined, venueTimeZone),
            tournamentBracketCells: buildTournamentBracketCellsForClassPlay(
              values,
              playBaselineRef.current ?? undefined,
            ),
            deletedBracketCellIds: values.pendingDeletedBracketCellIds ?? [],
          },
        ),
      );
      pendingDeleteStateRef.current = {
        bracketCells: {},
        pendingDeletedTopLevelMatchIds: [],
        pendingDeletedBracketCellIds: [],
      };
      await getClassData();
    } catch (e) {
      const err = e as Error;
      Notification(TypeError.error, err.message);
      await getClassData();
    }
  };

  const closeDownstreamDeleteConfirm = () => {
    pendingClassPlaySaveRef.current = null;
    setDownstreamDeleteConfirmOpen(false);
  };

  useEffect(() => {
    getClassData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, currentClassId]);

  const matches = Object.values(initialFormikData.tournamentMatches);

  return (
    <div className="px-2">
      {classCollection.length ? (
        <div>
          <Formik onSubmit={console.log} initialValues={{ classId: currentClassId || null }}>
            <form>
              <div className="d-flex align-items-center my-3">
                <h4 className="font-weight-bold mb-0">{t(prefix + 'classesTitle')}</h4>
              </div>
              <div className="raqt__select-wrapper">
                {loading && <div className="disabled-block"></div>}
                <ReactSelectForm
                  options={playOptions}
                  name="classId"
                  label={t(prefix + 'className')}
                />
                {matches.length > 0 && (
                  <ScoreCardDownload
                    matches={matches}
                    buttonText={t(buttonPrefix + 'downloadScoreCards')}
                  />
                )}
              </div>
            </form>
          </Formik>
          {(!loading && (
            <Formik
              enableReinitialize={true}
              initialValues={initialFormikData}
              validateOnChange={false}
              validateOnBlur={true}
              validateOnMount={false}
              onSubmit={async (values: PlayValues) => {
                if (typeof currentClassId !== 'number') return;
                const check = checkStartedDownstreamDeletes(
                  values,
                  playBaselineRef.current,
                );
                if (
                  check.hasStartedDownstream &&
                  !check.allStartedDownstreamCoveredByDelete
                ) {
                  Notification(
                    TypeError.error,
                    t('error-startedDownstreamMatchesBlockDelete'),
                  );
                  return;
                }
                if (
                  check.hasStartedDownstream &&
                  check.allStartedDownstreamCoveredByDelete
                ) {
                  pendingClassPlaySaveRef.current = values;
                  setDownstreamDeleteConfirmOpen(true);
                  return;
                }
                await saveClassPlay(values);
              }}
            >
              {(formikArg) => {
                pendingDeleteStateRef.current = {
                  bracketCells: formikArg.values.bracketCells,
                  pendingDeletedTopLevelMatchIds:
                    formikArg.values.pendingDeletedTopLevelMatchIds ?? [],
                  pendingDeletedBracketCellIds:
                    formikArg.values.pendingDeletedBracketCellIds ?? [],
                };
                // Generate calls `updatePlay(..., true)` → server uses persisted class groups only;
                // gate "Generate" on the same source as the API (not unsaved Formik groups).
                const groupsForGenerateReadiness = Array.isArray(currentClass?.tournamentGroups)
                  ? currentClass.tournamentGroups
                  : [];

                const hasMultiRoundGroupsOnClass =
                  groupsForGenerateReadiness.length > 0 &&
                  groupsHaveBoxLeagueRoundMeta(groupsForGenerateReadiness);

                const boxLeagueGenerateReady = isBoxLeagueMode
                  ? boxLeaguePlayGroupsReadyForGenerate(
                      groupsForGenerateReadiness,
                      { moveBox: Boolean(currentClass?.moveBox) },
                    )
                  : true;
                return (
                <Form autoComplete="off" id={TagIdsEnum.PLAY}>
                  {(currentClass?.format === ClassFormatEnum.GROUP ||
                    currentClass?.format === ClassFormatEnum.GROUP_BRACKET ||
                    currentClass?.format === ClassFormatEnum.BOX_LEAGUE ||
                    currentClass?.format === ClassFormatEnum.GROUP_ONLY ||
                    currentClass?.format === ClassFormatEnum.BOX_LEAGUE_ELIM) &&
                    !currentClass.parentClassId && (
                      <GroupSection
                        formikArg={formikArg}
                        currentClass={currentClass}
                        teams={currentClass?.teams ?? []}
                        isBoxLeague={isBoxLeagueMode}
                        onPlayDataRefresh={getClassData}
                      />
                    )}
                  <div className="d-flex justify-content-between align-items-center pt-5">
                    <h4 className="font-weight-bold mb-0">{t(prefix + 'matches')}</h4>
                    {isGenerateButton &&
                      currentClass?.parentClassId ? (
                      <p className="d-flex fw-lighter mb-0 justify-content-end">{t(prefix + 'warning9')}</p>
                    ) : (
                      (currentClass?.format !== ClassFormatEnum.GROUP &&
                        currentClass?.format !== ClassFormatEnum.GROUP_BRACKET &&
                        currentClass?.format !== ClassFormatEnum.BOX_LEAGUE &&
                        currentClass?.format !== ClassFormatEnum.GROUP_ONLY &&
                        currentClass?.format !== ClassFormatEnum.BOX_LEAGUE_ELIM) ||
                      formikArg.values.tournamentGroups.length > 0
                    ) &&
                    Object.values(formikArg.values.tournamentMatches).length === 0 && (
                      <CustomButton
                        variant="outline-dark"
                        btnText={t(buttonPrefix + 'generate')}
                        className="d-flex justify-content-between align-items-center ms-3"
                        disabled={
                          isBoxLeagueMode &&
                          !boxLeagueGenerateReady
                        }
                        title={
                          isBoxLeagueMode && !boxLeagueGenerateReady
                            ? currentClass?.moveBox
                              ? t(
                                  'creativeDetail-boxLeagueGenerateHintMoveBox',
                                  'Each round 1 box needs 4–10 distinct players with all pair-teams. Later ladder rounds can be empty until promotion. Save after editing.',
                                )
                              : hasMultiRoundGroupsOnClass
                                ? t(
                                    'creativeDetail-boxLeagueGenerateHintMultiRound',
                                    'Each round 1 box needs 4–10 distinct players with all pair-teams. Later ladder round groups may be empty until you save. Save after editing.',
                                  )
                                : t(
                                    'creativeDetail-boxLeagueGenerateHint',
                                    'Each box needs 4–10 distinct players with all pair-teams before generating. Save the class after editing groups.',
                                  )
                            : undefined
                        }
                        cb={async () => {
                          if (
                            isBoxLeagueMode &&
                            !boxLeagueGenerateReady
                          ) {
                            return;
                          }
                          if (typeof currentClassId !== 'number') return;
                          try {
                            await dispatch(updatePlay(currentClassId, [], true));
                            await getClassData();
                          } catch {
                            await getClassData();
                          }
                        }}
                      >
                        <AddIcon />
                      </CustomButton>
                    )}
                  </div>
                  <div className="small text-muted mb-2">
                    {t(prefix + 'tournamentDatesVenueTimezone', { timeZone: venueTimeZoneLabel })}
                  </div>
                  <MatchSection
                    formikArg={formikArg}
                    courts={courts}
                    onPlayDataRefresh={getClassData}
                  />
                </Form>
              );
              }}
            </Formik>
          )) || <div>Loading...</div>}
          <ConfirmModal
            opened={downstreamDeleteConfirmOpen}
            onHide={closeDownstreamDeleteConfirm}
            onCancel={closeDownstreamDeleteConfirm}
            onConfirm={() => {
              const held = pendingClassPlaySaveRef.current;
              closeDownstreamDeleteConfirm();
              if (held) {
                void saveClassPlay(held);
              }
            }}
            text={t('confirm-deleteStartedDownstreamMatches')}
            description={t('confirm-deleteStartedDownstreamMatchesDescription')}
          />
        </div>
      ) : (
        <h4 className="font-weight-bold no-classes">{t(prefix + 'warning2')}</h4>
      )}
    </div>
  );
};
